import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import CircularProgress from '@material-ui/core/CircularProgress';

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.background.default,
      height: '90vh',
      maxHeight: '90vh',
    },
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2),
  },
  content: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  actions: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5, 2),
  },
}));

interface FullScreenEditDialogProps {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
  busy?: boolean;
  error?: string | null;
  children: React.ReactNode;
  hideActions?: boolean;
  extraActions?: React.ReactNode;
}

const FullScreenEditDialog: React.FC<FullScreenEditDialogProps> = ({
  title,
  open,
  onClose,
  onSubmit,
  submitLabel = 'Update',
  busy = false,
  error,
  children,
  hideActions = false,
  extraActions,
}) => {
  const classes = useStyles();

  return (
    <Dialog maxWidth="lg" fullWidth open={open} onClose={onClose} className={classes.dialog}>
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant="h6">{title}</Typography>
        <IconButton edge="end" onClick={onClose} aria-label="close" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.content}>{children}</DialogContent>
      {!hideActions && (
        <DialogActions className={classes.actions}>
          {extraActions}
          {error && (
            <Box flex={1}>
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Box>
          )}
          {!extraActions && <Box flex={1} />}
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {onSubmit && (
            <Button
              variant="contained"
              color="primary"
              onClick={onSubmit}
              disabled={busy}
              startIcon={busy ? <CircularProgress size={16} /> : undefined}
            >
              {submitLabel}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default FullScreenEditDialog;
